import * as React from 'react'
import {
  Button,
  Icon,
  ErrorMessage,
  Text,
  Box,
  Column,
  Columns,
} from '@lightspeed/design-system-react'
import { CustomerBadge } from '@vend/react-utilities'
import { ApiCustomer } from '@vend/types'
import classNames from 'classnames'
import { useT } from '@transifex/react'
import {
  getDefaultContactOption,
  isInvalidEmail,
} from './customer-contact-utils'
import { ContactNumber } from './ContactNumber'
import { CONTACT_OPTIONS, PartialCustomerGroup, SOURCE_MODAL } from './types'
import { DottMessage } from './DottMessage'
import { useCustomerGroups } from './hooks/useCustomerGroups'
import { CustomerGroupsDropdown } from './CustomerGroupsDropdown'

interface Props {
  onSelect: (customer: ApiCustomer | null) => void
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedCustomer: ApiCustomer | null
  group?: PartialCustomerGroup
  sourceModal?: SOURCE_MODAL // used to customize the UI for various modals
}

export const SelectedCustomer: React.FC<Props> = ({
  onSelect,
  onContactChange,
  selectedCustomer,
  group,
  sourceModal,
}) => {
  const shouldShowEmailDott =
    sourceModal &&
    sourceModal === SOURCE_MODAL.SERVICE &&
    !selectedCustomer?.email
  const shouldShowErrorMessage =
    sourceModal === SOURCE_MODAL.SERVICE && isInvalidEmail(selectedCustomer)

  const allCustomerGroups = useCustomerGroups()

  const t = useT()

  const selectCustomerCopies = {
    next: t('Next', {
      _context:
        'Next button text for selected customer section in service modal.',
    }),
    email: t('Email', {
      _context: 'Input label for selected customer section in service modal.',
    }),
    contactNumber: t('Contact number', {
      _context: 'Input label for selected customer section in service modal.',
    }),
    customerGroup: t('Customer group', {
      _context: 'Input label for selected customer section in service modal.',
    }),
    emailPlaceholder: t('Enter email', {
      _context:
        'Input placeholder text for selected customer section in service modal.',
    }),
    errorMessage: t('Enter a valid email address', {
      _context: 'Error message for selected customer section in service modal.',
    }),
    dottMessage: t(
      `Add an email to speed up the process of contacting customers for pickup. Lightspeed Retail will automatically contact customers when their service is completed.`,
      {
        _context: 'Message for selected customer section in service modal.',
      }
    ),
  }

  return (
    <>
      <Box className="vd-flex vd-flex--justify-between vd-flex--align-center vd-pl1 vd-pr1">
        <CustomerBadge
          apiCustomer={selectedCustomer!}
          group={group}
          size="small"
        />
        <Button
          modifier="icon"
          variant="no"
          onClick={() => onSelect(null)}
          aria-label="Delete customer"
          data-track="remove-customer"
        >
          <Icon icon="fa-trash" className="fa fa-trash" />
        </Button>
      </Box>
      <Columns
        space={0}
        style={
          sourceModal === SOURCE_MODAL.SERVICE
            ? { flexFlow: 'row-reverse' }
            : {}
        }
        className="vd-pt4"
      >
        <Column width="6/12" className="vd-pl1 vd-pr1">
          <Box className="vd-pt2 vd-pb2">
            <Text type="label">{selectCustomerCopies.contactNumber}</Text>
          </Box>
          <ContactNumber
            customer={selectedCustomer}
            onChange={onContactChange}
            defaultOption={getDefaultContactOption(selectedCustomer)}
          />
        </Column>

        <Column width="6/12" className="vd-pl1 vd-pr1">
          <Box className="vd-pt2 vd-pb2">
            <Text type="label">{selectCustomerCopies.email}</Text>
          </Box>
          <div className="vd-value">
            <input
              className={classNames('vd-input', {
                'vd-input--error': isInvalidEmail(selectedCustomer),
                ' vd-dott-border': shouldShowEmailDott,
              })}
              placeholder={selectCustomerCopies.emailPlaceholder}
              value={selectedCustomer!.email || ''}
              onChange={onContactChange}
              name={CONTACT_OPTIONS.EMAIL}
            />
          </div>
          {shouldShowErrorMessage && (
            <ErrorMessage>{selectCustomerCopies.errorMessage}</ErrorMessage>
          )}
        </Column>
      </Columns>
      {sourceModal === SOURCE_MODAL.SERVICE && (
        <Columns space={0} className="vd-pt4">
          <Column width="6/12" className="vd-pl1 vd-pr1">
            <Box className="vd-pt2 vd-pb2">
              <Text type="label">{selectCustomerCopies.customerGroup}</Text>
            </Box>
            <CustomerGroupsDropdown
              group={group}
              onContactChange={onContactChange}
              customerGroups={allCustomerGroups}
            />
          </Column>
        </Columns>
      )}
      {shouldShowEmailDott && (
        <DottMessage>{selectCustomerCopies.dottMessage}</DottMessage>
      )}
    </>
  )
}
